import { useState, useEffect } from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { useGlobalStore } from "~/stores/store-provider";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  fileName: string;
}

const DeleteConfirmationModal = ({
  isOpen,
  fileName,
}: DeleteConfirmationModalProps) => {
  // Get functions directly from the slice
  const { confirmDelete, cancelDelete, isDeleting } = useGlobalStore(
    (state) => state
  );
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen ? "bg-black/40 opacity-100" : "bg-black/0 opacity-0"
      }`}
      onClick={cancelDelete}
    >
      <div
        className={`bg-base-100/80 backdrop-blur-md rounded-3xl shadow-xl border border-base-300/50 max-w-sm w-full transform transition-all duration-300 ${
          isOpen
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <div className="flex justify-end p-3 pb-0">
          <button
            onClick={cancelDelete}
            disabled={isDeleting}
            className="w-8 h-8 rounded-full flex items-center justify-center text-base-content/40 hover:text-base-content/60 hover:bg-base-200 transition-colors"
          >
            <IoClose className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 pt-2">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-error/10 rounded-full flex items-center justify-center">
              <RiDeleteBinLine className="w-6 h-6 text-error" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-base-content text-center mb-2">
            Delete File?
          </h3>

          {/* Description */}
          <div className="text-center mb-6">
            <p className="text-base-content/70 text-sm leading-relaxed mb-1">
              Are you sure you want to delete{" "}
              <span className="font-medium text-base-content">{fileName}</span>?
            </p>
            <p className="text-base-content/50 text-xs">
              This action cannot be undone.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={cancelDelete}
              disabled={isDeleting}
              className="btn btn-outline flex-1 hover:bg-base-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={isDeleting}
              className="btn btn-error flex-1 text-white hover:bg-error/90"
            >
              {isDeleting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <RiDeleteBinLine className="w-4 h-4" />
                  Delete File
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
